import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { clientQuery, clientMutation } from "Core/data/GraphQLApi";
import { catalogProcessItem } from '../../../../../../../core/catalogs/redux/actions';
import { endpoints } from "../../../../../../../core/defaultValues";
import { ExceptionManager } from "../../../../../../../core/logManager";


const INIT_STATE = {
    open: false,
    statuses: null,
    loading: false,
    event: null,
    events: [],
    error: null,
    savedStatus: null,
    multipleEvents: false,
};


export const searchStatuses = createAsyncThunk(
    "driverPerformance/views/events/modals/assingStatus/redux/searchStatuses",
    async (payload, { rejectWithValue, getState }) => {
        try {

            const body = {
                conditions: [
                    {
                        fields: ["entityName"],
                        terms: ["eventStatus"],
                    },
                ],
                limit: 100,
                offset: 0,
                sort: [{ field: "label", order: "ASC" }],
            };

            const response = await clientQuery(
                `query searchEntityLists ($conditions: [EntityListSearchInput], $sort: [EntityListSortInput], $limit: Int, $offset: Int) {
                    data: searchEntityLists (conditions: $conditions, sort: $sort, limit: $limit, offset: $offset) {
                        items {
                            entityId
                            entityName
                            parentId
                            label
                            value1
                            value2
                        }
                        total
                    }
                }`,
                body,
                endpoints.GRAPHQL_GENERAL_V2
            );

            return response?.data?.items || [];


        } catch (exc) {
            return rejectWithValue(exc.message);
        }
    }
);

export const assignStatusesToEvent = createAsyncThunk(
    'driverPerformance/assignStatus/assign',
    async ({ rowId, status, esn }, { rejectWithValue, dispatch }) => {
        try {
            const result = await clientMutation(
                `
                mutation updateCaseEventFlags(
                    $unitId: String!
                    $rowId: ID!
                    $caseEventStatus: String
                ) {
                    updateCaseEventFlags(
                        unitId: $unitId
                        rowId: $rowId
                        caseEventStatus: $caseEventStatus
                    )
                }
                `,
                { 
                    unitId: esn, // ESN is the unitId
                    rowId: rowId,
                    caseEventStatus: status?.entityId || status // Status value (e.g., "verified", "noissue", "falsepositive")
                },
                endpoints.GRAPHQL_GENERAL_V2
            );

            // Update local state if successful
            if (result?.updateCaseEventFlags === true) {
                return { success: true, rowId, status };
            }

            throw new Error('Failed to update event status');

        } catch (exc) {
            ExceptionManager(exc, 'driverPerformance/views/events/modals/assignStatus', 'assignStatusesToEvent');
            return rejectWithValue(exc.message);
        }
    }
);

export const assignStatusesToEventBulk = createAsyncThunk(
    'driverPerformance/assignStatus/assign/bulk',
    async ({ events, status }, { rejectWithValue, dispatch }) => {
        try {

            const eventIds = events.map(event => ({
                unitId: event?.esn || event?.unitId,
                rowId: event.rowId
            }));
            const result = await clientMutation(
                `
                mutation updateCaseEventsBulkFlags(
                    $eventIds: [EntryEventInput]!
                    $caseEventStatus: String
                ) {
                    updateCaseEventsBulkFlags(
                        eventIds: $eventIds
                        caseEventStatus: $caseEventStatus
                    )
                }
                `,
                { 
                    eventIds: eventIds,
                    caseEventStatus: status?.entityId || status, // e.g., "verified", "noissue", "falsepositive"
                    
                },
                endpoints.GRAPHQL_GENERAL_V2
            );

            // Update local state if successful
            if (result?.updateCaseEventsBulkFlags === true) {
                return { success: true, events, status };
            }

            throw new Error('Failed to update event status in bulk');

        } catch (exc) {
            ExceptionManager(exc, 'driverPerformance/views/events/modals/assignStatus', 'assignStatusesToEventBulk');
            return rejectWithValue(exc.message);
        }
    }
);

const driverPerformanceEventsTabAssignStatusSliceRedux = createSlice({
    name: 'driverPerformanceAssignStatus',
    initialState: INIT_STATE,
    reducers: {
        reset(state, action) {
            return action.payload ? { ...state, ...action.payload } : { ...INIT_STATE }
        },
        openModalAssignStatus: (state, action) => {
            state.open = true;
            state.event = action.payload.event;
            state.events = action.payload.events || [];
            state.multipleEvents = action.payload.multipleEvents || false;
        },
        closeModalAssignStatus: (state) => {
            state.open = false;
            state.event = null;
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(searchStatuses.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(searchStatuses.fulfilled, (state, action) => {
            state.loading = false;
            state.statuses = action.payload;
        });
        builder.addCase(searchStatuses.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        builder.addCase(assignStatusesToEvent.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(assignStatusesToEvent.fulfilled, (state, action) => {
            state.loading = false;
            state.savedStatus = action.payload;
        });
        builder.addCase(assignStatusesToEvent.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
        
        builder.addCase(assignStatusesToEventBulk.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(assignStatusesToEventBulk.fulfilled, (state, action) => {
            state.success = action.payload;
            state.loading = false;
            state.savedStatus = action.payload;
        });
        builder.addCase(assignStatusesToEventBulk.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
}
});

// Extract and export the action creators object and the reducer
export const { actions, reducer } = driverPerformanceEventsTabAssignStatusSliceRedux;

// Extract and export each action creator by name
export const { reset, openModalAssignStatus, closeModalAssignStatus } = actions;

// Export the reducer, either as a default or named export
export default reducer;
